import React, { useEffect, useState, useCallback } from "react";
import { useApp, api } from "@/context/AppProvider";
import { Reveal } from "@/components/motion";
import { EditableText } from "@/components/editable";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Award, Plus, Trash2, FileText, UploadCloud, ArrowUp, ArrowDown } from "lucide-react";

const inputStyle =
  "bg-transparent border-0 border-b rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-b-2 shadow-none";

export const Certifications = () => {
  const { tr, lang, user, editMode } = useApp();
  const isAdmin = !!user;

  const [certs, setCerts] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/certifications");
      setCerts(data);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const [form, setForm] = useState({ title_en: "", title_ar: "", issuer_en: "", issuer_ar: "", year: "" });
  const [fileData, setFileData] = useState("");
  const [fileType, setFileType] = useState("image");
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(lang === "ar" ? "الملف كبير جداً (الحد ٨ ميجابايت)" : "File too large (max 8MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFileData(reader.result);
      setFileType(file.type === "application/pdf" ? "pdf" : "image");
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!fileData) {
      toast.error(lang === "ar" ? "يرجى اختيار ملف" : "Please select a file");
      return;
    }
    setSaving(true);
    try {
      await api.post("/certifications", { ...form, file_data: fileData, file_type: fileType });
      toast.success(lang === "ar" ? "تمت إضافة الشهادة" : "Certification added");
      setUploadOpen(false);
      setForm({ title_en: "", title_ar: "", issuer_en: "", issuer_ar: "", year: "" });
      setFileData("");
      load();
    } catch {
      toast.error(tr.contact.error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/certifications/${id}`);
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
      load();
    } catch {
      toast.error(tr.contact.error);
    }
  };

  const handleMove = async (index, delta) => {
    const j = index + delta;
    if (j < 0 || j >= certs.length) return;
    const next = [...certs];
    [next[index], next[j]] = [next[j], next[index]];
    setCerts(next);
    try {
      await api.put("/certifications/reorder", { ids: next.map((c) => c.id) });
    } catch {
      load();
    }
  };

  return (
    <section id="certifications" data-testid="certifications" className="relative py-24 md:py-32 lg:py-40" style={{ backgroundColor: "var(--bone)" }}>
      <div className="container-lux">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <Reveal className="max-w-2xl">
            <EditableText as="p" path={["certifications", "eyebrow"]} value={tr.certifications.eyebrow} className="eyebrow mb-4 inline-block" />
            <EditableText as="h2" path={["certifications", "title"]} value={tr.certifications.title} className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.1] block" style={{ color: "var(--ink)" }} />
            <EditableText as="p" multiline path={["certifications", "subtitle"]} value={tr.certifications.subtitle} className="font-body text-base mt-4 block" style={{ color: "var(--ink-soft)" }} />
          </Reveal>

          {isAdmin && (
            <button
              data-testid="upload-cert-btn"
              onClick={() => setUploadOpen(true)}
              className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-body font-medium text-white shrink-0"
              style={{ backgroundColor: "var(--ink)" }}
            >
              <Plus size={16} /> {tr.certifications.uploadBtn}
            </button>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="certifications-grid">
          {certs.map((c, i) => (
            <Reveal key={c.id} delay={(i % 3) * 0.08}>
              <div className="group relative h-full rounded-2xl p-7 transition-all duration-500 hover:-translate-y-1 shadow-soft hover:shadow-lift" style={{ backgroundColor: "var(--ivory)", border: "1px solid rgba(19,19,19,0.06)" }}>
                {c.file_data && c.file_type === "image" ? (
                  <div className="mb-5 overflow-hidden rounded-xl" style={{ aspectRatio: "4/3", backgroundColor: "var(--sand)" }}>
                    <img src={c.file_data} alt={c.title_en} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                ) : (
                  <div className="mb-5 w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(197,160,89,0.12)" }}>
                    <Award size={22} style={{ color: "var(--gold)" }} />
                  </div>
                )}

                {c.year && <p className="text-[11px] uppercase tracking-[0.25em] mb-2" style={{ color: "var(--gold)" }}>{c.year}</p>}
                <h3 className="font-display text-xl mb-1 leading-snug" style={{ color: "var(--ink)" }}>
                  {lang === "ar" && c.title_ar ? c.title_ar : c.title_en}
                </h3>
                <p className="font-body text-sm" style={{ color: "var(--ink-soft)" }}>
                  {lang === "ar" && c.issuer_ar ? c.issuer_ar : c.issuer_en}
                </p>

                {c.file_data && c.file_type === "pdf" && (
                  <a href={c.file_data} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm font-body" style={{ color: "var(--gold)" }}>
                    <FileText size={15} /> {tr.certifications.viewFile}
                  </a>
                )}

                {isAdmin && editMode && (
                  <div className="edit-controls absolute top-4 end-4">
                    <button onClick={() => handleMove(i, -1)} disabled={i === 0} className="ec-btn" data-testid={`cert-up-${c.id}`}><ArrowUp size={14} /></button>
                    <button onClick={() => handleMove(i, 1)} disabled={i === certs.length - 1} className="ec-btn" data-testid={`cert-down-${c.id}`}><ArrowDown size={14} /></button>
                    <button onClick={() => handleDelete(c.id)} className="ec-btn ec-danger" data-testid={`delete-cert-${c.id}`}><Trash2 size={14} /></button>
                  </div>
                )}
                {isAdmin && !editMode && (
                  <button
                    data-testid={`delete-cert-${c.id}`}
                    onClick={() => handleDelete(c.id)}
                    className="absolute top-4 end-4 w-9 h-9 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: "rgba(180,30,30,0.08)", color: "#b41e1e" }}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{tr.admin.uploadTitle}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{tr.admin.titleEn}</Label>
                <Input data-testid="cert-title-en" required value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className={inputStyle} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{tr.admin.titleAr}</Label>
                <Input data-testid="cert-title-ar" dir="rtl" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} className={inputStyle} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{tr.admin.issuerEn}</Label>
                <Input data-testid="cert-issuer-en" value={form.issuer_en} onChange={(e) => setForm({ ...form, issuer_en: e.target.value })} className={inputStyle} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{tr.admin.issuerAr}</Label>
                <Input data-testid="cert-issuer-ar" dir="rtl" value={form.issuer_ar} onChange={(e) => setForm({ ...form, issuer_ar: e.target.value })} className={inputStyle} />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{tr.admin.year}</Label>
              <Input data-testid="cert-year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className={inputStyle} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: "var(--ink-soft)" }}>{tr.admin.file}</Label>
              <label className="flex items-center gap-3 cursor-pointer rounded-xl px-4 py-4 transition-colors" style={{ border: "1px dashed rgba(19,19,19,0.25)" }}>
                <UploadCloud size={20} style={{ color: "var(--gold)" }} />
                <span className="text-sm font-body" style={{ color: "var(--ink-soft)" }}>
                  {fileData ? (fileType === "pdf" ? "PDF selected" : "Image selected") : (lang === "ar" ? "اختر صورة أو PDF" : "Choose image or PDF")}
                </span>
                <input data-testid="cert-file" type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" />
              </label>
            </div>
            <button data-testid="cert-save" type="submit" disabled={saving} className="w-full rounded-full py-3.5 text-white text-sm font-body font-medium disabled:opacity-60" style={{ backgroundColor: "var(--ink)" }}>
              {saving ? tr.admin.saving : tr.admin.save}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
